"use client"; // If using Next.js App Router

import { zodResolver } from '@hookform/resolvers/zod';
import { user as PrismaUser, store as Store } from '@prisma/client'; // Added Store for availableStores typing
import {
    AlertCircle,
    ArrowLeft,
    ChevronsUpDown,
    Loader2,
    RotateCcw,
    Save,
    Trash2,
    UserRound as UserIcon, // Renamed to avoid conflict
    X,
} from 'lucide-react';
import React, { useEffect, useState } from 'react'; // Added React import
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom'; // Corrected useNavigate import
import { toast } from 'sonner';
import { z } from 'zod';
import { trpc } from "~/api/trpc";
import useAuth from '~/auth/useAuth';
import { Alert, AlertDescription, AlertTitle } from '~/components/ui/alert'; // Added Alert components
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '~/components/ui/alert-dialog';
import { Button } from "~/components/ui/button";
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle
} from "~/components/ui/card";
import { Checkbox } from "~/components/ui/checkbox";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '~/components/ui/command';
import {
    Form,
    FormControl,
    FormDescription,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "~/components/ui/form";
import { Input } from "~/components/ui/input";
import { passwordSchema } from '~/lib/passwordPolicy';
import { Popover, PopoverContent, PopoverTrigger } from '~/components/ui/popover';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "~/components/ui/select";
import { Separator } from '~/components/ui/separator';
import { Timezone } from '~/types/server-types/Timezone';
import { UserRole } from '~/types/server-types/UserRole';
import { PractitionerNameWithCategory } from '~/components/practitioners/PractitionerNameWithCategory';

// Zod schema for form validation (remains the same)
const userFormSchema = z.object({
    firstName: z.string().min(1, "First name is required"),
    lastName: z.string(),
    companyName: z.string().optional().nullable(),
    street: z.string().optional().nullable(),
    streetNumber: z.string().optional().nullable(),
    timezone: z.nativeEnum(Timezone),
    zipCode: z.string().optional().nullable(),
    city: z.string().optional().nullable(),
    email: z.string().email("Invalid email address"),
    phone: z.string().optional().nullable(),
    role: z.string().transform((val, ctx) => {
        if (!val) return undefined;

        // Check if the value is in the UserRole enum
        if (Object.values(UserRole).includes(val as any)) {
            return val as UserRole;
        }

        // If not, add an issue to the context
        ctx.addIssue({
            code: 'invalid_value',
            expected: 'enum',
            input: Object.values(UserRole),
            values: Object.values(UserRole),
            received: val,
            path: ['role'],
            message: `Invalid role. Must be one of: ${Object.values(UserRole).join(', ')}`
        });

        // Return undefined to fail the transformation
        return undefined;
    }).optional(),
    active: z.boolean(),
    newPassword: passwordSchema.optional().or(z.literal('')),
    confirmPassword: z.string().optional().or(z.literal('')),
    storeIds: z.array(z.number()).optional().default([]),
    practitionerId: z.number().optional().nullable().default(null),
}).refine(data => {
    if (data.newPassword && data.newPassword !== data.confirmPassword) {
        return false;
    }
    return true;
}, {
    message: "Passwords don't match",
    path: ["confirmPassword"],
});

type UserFormData = z.infer<typeof userFormSchema>;

function getEnumKeys<T extends string, TEnumValue extends string>(enumVariable: { [key in T]: TEnumValue }) {
    return Object.keys(enumVariable) as T[];
}

// Define a more specific type for the user data fetched by TRPC
type FetchedUser = PrismaUser & {
    storeAssociations: { storeId: number }[];
    createdAt: string | Date; // TRPC might return string, ensure it's Date for form
    updatedAt: string | Date;
    deletedAt?: string | Date | null;
};


const User = () => {
    const params = useParams();
    const navigate = useNavigate();
    const { roles } = useAuth();
    const isAdmin = roles.includes('ADMIN');
    const utils = trpc.useUtils();

    const userId = parseInt(params.id!, 10);

    const [isStoreMultiSelectOpen, setIsStoreMultiSelectOpen] = useState(false);
    const [isPractitionerSelectOpen, setIsPractitionerSelectOpen] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [isRestoreDialogOpen, setIsRestoreDialogOpen] = useState(false);

    const {
        data: rawUserData,
        isLoading: isLoadingUser,
        error: userError,
        refetch: refetchUser
    } = trpc.user.getUserById.useQuery(
        { id: userId },
        {
            enabled: !isNaN(userId),
        }
    );

    React.useEffect(() => {
        if (rawUserData) {
            console.log("Raw User Data:", rawUserData.timezone, rawUserData.role);
        }
    }, [rawUserData]);

    const userData = React.useMemo(() => {
        if (!rawUserData) return null;
        const processed = {
            ...rawUserData,
            role: rawUserData.role as UserRole,
            createdAt: new Date(rawUserData.createdAt),
            updatedAt: new Date(rawUserData.updatedAt),
            deletedAt: rawUserData.deletedAt ? new Date(rawUserData.deletedAt) : null,
        } as FetchedUser;
        console.log("Processed UserData (useMemo):", processed.timezone, processed.role);
        return processed;
    }, [rawUserData]);


    const { data: storesData, isLoading: isLoadingStores } = trpc.stores.getAll.useQuery(
        { pageSize: 1000 },
        { staleTime: 1000 * 60 * 10 }
    );
    const availableStores: Store[] = storesData?.data.map((s) => ({
        ...s,
        createdAt: new Date(s.createdAt),
        updatedAt: new Date(s.updatedAt),
        deletedAt: s.deletedAt ? new Date(s.deletedAt) : null,
    })) || [];

    const { data: practitionersData, isLoading: isLoadingPractitioners } = trpc.practitioners.getAll.useQuery(
        { pageSize: 1000 },
        { staleTime: 1000 * 60 * 10 }
    );
    const availablePractitioners = practitionersData?.data.map((practitioner) => ({
        id: practitioner.id,
        name: `${!!practitioner.title ? `${practitioner.title} ` : ''} ${practitioner.firstName} ${practitioner.lastName}`,
        categoryName: practitioner.practitioner_category?.name ?? null,
        alreadyAssignedUser: !!practitioner.user ? {
            id: practitioner.user.id,
            name: `${practitioner.user.firstName} ${practitioner.user.lastName}`
        } : null,
    })) || [];

    const updateUserMutation = trpc.user.updateUser.useMutation({
        onSuccess: (updatedUserResponse) => {
            toast.success("User Updated", {
                description: `User ${updatedUserResponse.firstName} ${updatedUserResponse.lastName} has been updated successfully.`,
            });
            // Ensure dates are proper Date objects before resetting
            const transformedUpdatedUser = {
                ...updatedUserResponse,
                createdAt: new Date(updatedUserResponse.createdAt),
                updatedAt: new Date(updatedUserResponse.updatedAt),
                deletedAt: updatedUserResponse.deletedAt ? new Date(updatedUserResponse.deletedAt) : null,
            };
            form.reset({
                ...transformPrismaUserToFormData(transformedUpdatedUser as FetchedUser),
                newPassword: '',
                confirmPassword: '',
            });
            refetchUser(); // Refetch to get the latest data including any server-side transformations
        },
        onError: (error) => {
            toast.error("Error updating user", {
                description: error.message,
            });
        },
    });

    const deleteUserMutation = trpc.user.deleteUser.useMutation({
        onSuccess: () => {
            toast.success("User deleted", {
                description: "The user has been soft deleted and can no longer log in.",
            });
            setIsDeleteDialogOpen(false);
            utils.user.getAll.invalidate();
            refetchUser();
        },
        onError: (error) => {
            toast.error("Error deleting user", {
                description: error.message,
            });
        },
    });

    const restoreUserMutation = trpc.user.restoreUser.useMutation({
        onSuccess: () => {
            toast.success("User restored", {
                description: "The user has been restored and can log in again.",
            });
            setIsRestoreDialogOpen(false);
            utils.user.getAll.invalidate();
            refetchUser();
        },
        onError: (error) => {
            toast.error("Error restoring user", {
                description: error.message,
            });
        },
    });

    const form = useForm<UserFormData>({
        resolver: zodResolver(userFormSchema),
        defaultValues: {
            firstName: '',
            lastName: '',
            companyName: '',
            street: '',
            streetNumber: '',
            zipCode: '',
            city: '',
            email: '',
            phone: '',
            active: false,
            newPassword: '',
            confirmPassword: '',
            storeIds: [],
            practitionerId: null,
            timezone: Timezone.Europe_Berlin,
            role: UserRole.USER,
        },
    });

    const { formState: { isDirty }, control, handleSubmit, reset } = form;

    const transformPrismaUserToFormData = (user: FetchedUser | null | undefined): Partial<UserFormData> => {
        if (!user) return {};
        const formData = {
            firstName: user.firstName,
            lastName: user.lastName,
            companyName: user.companyName,
            street: user.street,
            streetNumber: user.streetNumber,
            zipCode: user.zipCode,
            city: user.city,
            email: user.email,
            phone: user.phone,
            timezone: user.timezone,
            role: user.role,
            active: user.active,
            storeIds: user.storeAssociations?.map(sa => sa.storeId) || [],
            practitionerId: user.practitionerId || null,
        };

        console.log("Transforming Prisma User to FormData:", formData.timezone, formData.role);

        return formData;
    };

    useEffect(() => {
        if (userData) {
            const formDataToReset = transformPrismaUserToFormData(userData);
            console.log("Calling form.reset with:", formDataToReset.timezone, formDataToReset.role);
            form.reset(formDataToReset);
        }
    }, [userData, form.reset]);

    const onSubmit = (data: UserFormData) => {
        const { confirmPassword, newPassword, ...restOfData } = data;
        const payload: Parameters<typeof updateUserMutation.mutate>[0] = {
            id: userId,
            ...restOfData,
        };
        if (newPassword && newPassword.trim() !== '') {
            payload.newPassword = newPassword;
        } else {
            // If newPassword is empty or just whitespace, ensure it's not sent
            // The backend already handles if newPassword is not in input.
            // This ensures an empty string from the form doesn't cause issues if Zod schema changes.
            delete payload.newPassword;
        }
        updateUserMutation.mutate(payload);
    };

    const handleDiscardChanges = () => {
        if (userData) {
            reset(transformPrismaUserToFormData(userData));
            toast.info("Changes discarded.");
        }
    };


    if (isLoadingUser || (isNaN(userId) && !userError)) { // Also check !userError to avoid brief loading on error
        return (
            <div className="flex items-center justify-center h-screen"> {/* Full screen loader */}
                <Loader2 className="h-10 w-10 animate-spin text-primary" />
            </div>
        );
    }

    if (userError || (!userData && !isLoadingUser)) { // Handle case where !userData after loading
        return (
            <div className="container mx-auto p-4 md:p-6 lg:p-8">
                <Alert variant="destructive" className="m-4">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Error Fetching User</AlertTitle>
                    <AlertDescription>
                        {userError?.message || 'Could not load user data. The user might not exist.'}
                        {!isNaN(userId) && (
                            <Button variant="link" onClick={() => refetchUser()} className="p-0 h-auto ml-2">Retry</Button>
                        )}
                        <Button variant="link" onClick={() => navigate('/users')} className="p-0 h-auto ml-2">Back to list</Button>
                    </AlertDescription>
                </Alert>
            </div>
        );
    }

    // This should ideally not be reached if error handling above is correct
    // but as a safeguard:
    if (!userData) {
        return (
            <div className="container mx-auto p-4 md:p-6 lg:p-8">
                <Alert variant="destructive" className="m-4">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>User Not Found</AlertTitle>
                    <AlertDescription>
                        The requested user could not be found.
                        <Button variant="link" onClick={() => navigate('/users')} className="p-0 h-auto ml-2">Back to list</Button>
                    </AlertDescription>
                </Alert>
            </div>
        );
    }


    const timezoneOptions = getEnumKeys(Timezone);
    const roleOptions = getEnumKeys(UserRole);

    return (
        <React.Fragment>
            <div className="container mx-auto p-4 md:p-6 lg:p-8">
                {/* --- Header --- */}
                <div className="flex flex-wrap justify-between items-center mb-6 gap-4">
                    <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
                        <Button
                            variant="ghost"
                            size="icon"
                            className='p-2 hover:bg-gray-200 dark:hover:bg-gray-700 transition-all cursor-pointer'
                            onClick={() => navigate("/users")} // Adjust this path as needed
                        >
                            <ArrowLeft className="h-5 w-5" />
                        </Button>
                        <UserIcon className="h-6 w-6" />
                        {userData.firstName} {userData.lastName}
                    </h1>
                    {isAdmin && (
                        userData.deletedAt ? (
                            <Button
                                variant="outline"
                                type="button"
                                onClick={() => setIsRestoreDialogOpen(true)}
                                disabled={restoreUserMutation.isPending}
                            >
                                {restoreUserMutation.isPending ? (
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                ) : (
                                    <RotateCcw className="mr-2 h-4 w-4" />
                                )}
                                Restore User
                            </Button>
                        ) : (
                            <Button
                                variant="destructive"
                                type="button"
                                onClick={() => setIsDeleteDialogOpen(true)}
                                disabled={deleteUserMutation.isPending}
                            >
                                {deleteUserMutation.isPending ? (
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                ) : (
                                    <Trash2 className="mr-2 h-4 w-4" />
                                )}
                                Delete User
                            </Button>
                        )
                    )}
                </div>

                {userData.deletedAt && (
                    <Alert variant="destructive" className="mb-6">
                        <AlertCircle className="h-4 w-4" />
                        <AlertTitle>This user is deleted</AlertTitle>
                        <AlertDescription>
                            This user was deleted on {new Date(userData.deletedAt).toLocaleString()} and can no longer log in.
                            {isAdmin && ' Use "Restore User" to reactivate the account.'}
                        </AlertDescription>
                    </Alert>
                )}

                {/* --- Main Content --- */}
                {/* Using a single column spanning full width for the form card */}
                <div className="grid grid-cols-1 gap-6">
                    <div className="lg:col-span-1"> {/* Or lg:col-span-2 if you had a right column */}
                        <Form {...form}>
                            <form onSubmit={handleSubmit(onSubmit)}>
                                <Card>
                                    <CardHeader>
                                        <CardTitle>Edit User Details</CardTitle>
                                        <CardDescription>Modify the user's information, settings, and store associations.</CardDescription>
                                    </CardHeader>
                                    <CardContent className="space-y-6">
                                        {/* Personal Information */}
                                        <p className="text-md font-semibold text-muted-foreground">Personal Information</p>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <FormField control={control} name="firstName" render={({ field }) => (<FormItem><FormLabel>First Name</FormLabel><FormControl><Input placeholder="John" {...field} /></FormControl><FormMessage /></FormItem>)} />
                                            <FormField control={control} name="lastName" render={({ field }) => (<FormItem><FormLabel>Last Name</FormLabel><FormControl><Input placeholder="Doe" {...field} /></FormControl><FormMessage /></FormItem>)} />
                                        </div>
                                        <FormField control={control} name="email" render={({ field }) => (<FormItem><FormLabel>Email</FormLabel><FormControl><Input type="email" placeholder="john.doe@example.com" {...field} /></FormControl><FormMessage /></FormItem>)} />
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <FormField control={control} name="phone" render={({ field }) => (<FormItem><FormLabel>Phone</FormLabel><FormControl><Input placeholder="+1234567890" {...field} value={field.value ?? ''} /></FormControl><FormMessage /></FormItem>)} />
                                            <FormField control={control} name="companyName" render={({ field }) => (<FormItem><FormLabel>Company Name (Optional)</FormLabel><FormControl><Input placeholder="Acme Corp" {...field} value={field.value ?? ''} /></FormControl><FormMessage /></FormItem>)} />
                                        </div>

                                        <Separator className="my-4" />
                                        <p className="text-md font-semibold text-muted-foreground">Address</p>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <FormField control={control} name="street" render={({ field }) => (<FormItem><FormLabel>Street (Optional)</FormLabel><FormControl><Input placeholder="Main St" {...field} value={field.value ?? ''} /></FormControl><FormMessage /></FormItem>)} />
                                            <FormField control={control} name="streetNumber" render={({ field }) => (<FormItem><FormLabel>Street Number (Optional)</FormLabel><FormControl><Input placeholder="123" {...field} value={field.value ?? ''} /></FormControl><FormMessage /></FormItem>)} />
                                            <FormField control={control} name="zipCode" render={({ field }) => (<FormItem><FormLabel>ZIP Code (Optional)</FormLabel><FormControl><Input placeholder="12345" {...field} value={field.value ?? ''} /></FormControl><FormMessage /></FormItem>)} />
                                            <FormField control={control} name="city" render={({ field }) => (<FormItem><FormLabel>City (Optional)</FormLabel><FormControl><Input placeholder="Anytown" {...field} value={field.value ?? ''} /></FormControl><FormMessage /></FormItem>)} />
                                        </div>

                                        <Separator className="my-4" />
                                        <p className="text-md font-semibold text-muted-foreground">Settings</p>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <FormField
                                                control={control}
                                                name="timezone"
                                                render={({ field }) => {
                                                    // ADD THIS LOG:
                                                    console.log(`RENDER Timezone - field.value: '${field.value}', typeof: ${typeof field.value}`);
                                                    return (
                                                        <FormItem>
                                                            <FormLabel>Timezone</FormLabel>
                                                            <Select
                                                                onValueChange={field.onChange}
                                                                value={field.value}
                                                                key={`timezone-${field.value}`}
                                                            >
                                                                <FormControl>
                                                                    <SelectTrigger>
                                                                        <SelectValue placeholder="Select timezone" />
                                                                    </SelectTrigger>
                                                                </FormControl>
                                                                <SelectContent>
                                                                    {timezoneOptions.map(tzKey => (
                                                                        <SelectItem key={tzKey} value={Timezone[tzKey]}>
                                                                            {Timezone[tzKey].replace(/_/g, ' ')}
                                                                        </SelectItem>
                                                                    ))}
                                                                </SelectContent>
                                                            </Select>
                                                            <FormMessage />
                                                        </FormItem>
                                                    );
                                                }}
                                            />
                                            <FormField
                                                control={form.control} // or just control
                                                name="role"
                                                render={({ field }) => {
                                                    console.log(`RENDER Role - field.value: '${field.value}', typeof: ${typeof field.value}`);
                                                    return (
                                                        <FormItem>
                                                            <FormLabel>Role</FormLabel>
                                                            <Select
                                                                onValueChange={field.onChange}
                                                                value={field.value}
                                                                key={`role-${field.value}`}
                                                            >
                                                                <FormControl>
                                                                    <SelectTrigger>
                                                                        <SelectValue placeholder="Select role" />
                                                                    </SelectTrigger>
                                                                </FormControl>
                                                                <SelectContent>
                                                                    {/* Minor correction for consistency: display UserRole[roleKey] like timezone */}
                                                                    {roleOptions.map(roleKey => (
                                                                        <SelectItem key={roleKey} value={UserRole[roleKey]}>
                                                                            {UserRole[roleKey]} {/* Displaying the actual enum value string */}
                                                                        </SelectItem>
                                                                    ))}
                                                                </SelectContent>
                                                            </Select>
                                                            <FormMessage />
                                                        </FormItem>
                                                    );
                                                }}
                                            />
                                        </div>

                                        {/* Active field - currently hidden as per your original className */}
                                        <FormField
                                            control={control}
                                            name="active"
                                            render={({ field }) => (
                                                <FormItem className="hidden flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                                                    <FormControl><Checkbox checked={field.value} onCheckedChange={field.onChange} /></FormControl>
                                                    <div className="space-y-1 leading-none"><FormLabel>Active</FormLabel><FormDescription>Controls whether the user can log in.</FormDescription></div>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />

                                        <Separator className="my-4" />
                                        <p className="text-md font-semibold text-muted-foreground">Change Password (Optional)</p>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <FormField control={control} name="newPassword" render={({ field }) => (<FormItem><FormLabel>New Password</FormLabel><FormControl><Input type="password" placeholder="Leave blank to keep current" {...field} /></FormControl><FormMessage /></FormItem>)} />
                                            <FormField control={control} name="confirmPassword" render={({ field }) => (<FormItem><FormLabel>Confirm New Password</FormLabel><FormControl><Input type="password" placeholder="Confirm new password" {...field} /></FormControl><FormMessage /></FormItem>)} />
                                        </div>
                                        <div className="text-sm text-muted-foreground">
                                            <p className="font-medium">Passwortanforderungen:</p>
                                            <p className="mt-1">
                                                Mindestens 8 Zeichen, davon ein Großbuchstabe, ein Kleinbuchstabe, eine Zahl und ein Sonderzeichen
                                            </p>
                                        </div>

                                        <Separator className="my-4" />
                                        <p className="text-md font-semibold text-muted-foreground">Store Associations</p>
                                        <FormField
                                            control={control}
                                            name="storeIds"
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel>Associated Stores</FormLabel>
                                                    <Popover open={isStoreMultiSelectOpen} onOpenChange={setIsStoreMultiSelectOpen}>
                                                        <PopoverTrigger asChild>
                                                            <FormControl>
                                                                <Button variant="outline" role="combobox" aria-expanded={isStoreMultiSelectOpen} className="w-full justify-between">
                                                                    {field.value && field.value.length > 0 ? `${field.value.length} store(s) selected` : "Select stores..."}
                                                                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                                                </Button>
                                                            </FormControl>
                                                        </PopoverTrigger>
                                                        <PopoverContent className="w-[--radix-popover-trigger-width] max-h-[300px] overflow-y-auto p-0">
                                                            <Command>
                                                                <CommandInput placeholder="Search stores..." />
                                                                <CommandList>
                                                                    <CommandEmpty>No stores found.</CommandEmpty>
                                                                    <CommandGroup>
                                                                        {isLoadingStores ? <CommandItem disabled>Loading stores...</CommandItem> :
                                                                            availableStores.map((store) => {
                                                                                const isSelected = field.value?.includes(store.id);
                                                                                return (
                                                                                    <CommandItem
                                                                                        key={store.id}
                                                                                        value={store.name}
                                                                                        onSelect={() => {
                                                                                            const currentSelectedIds = field.value || [];
                                                                                            if (isSelected) {
                                                                                                field.onChange(currentSelectedIds.filter(id => id !== store.id));
                                                                                            } else {
                                                                                                field.onChange([...currentSelectedIds, store.id]);
                                                                                            }
                                                                                        }}
                                                                                    >
                                                                                        <Checkbox className="mr-2" checked={isSelected} onCheckedChange={(checked) => { /* Handled by onSelect now */ }} />
                                                                                        <span>{store.name} ({store.city})</span>
                                                                                    </CommandItem>
                                                                                );
                                                                            })}
                                                                    </CommandGroup>
                                                                </CommandList>
                                                            </Command>
                                                        </PopoverContent>
                                                    </Popover>
                                                    <FormDescription>Select the stores this user should be associated with.</FormDescription>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />

                                        <FormField
                                            name="practitionerId"
                                            control={control}
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel>Associated Practitioner</FormLabel>
                                                    <Popover open={isPractitionerSelectOpen} onOpenChange={setIsPractitionerSelectOpen}>
                                                        <PopoverTrigger asChild>
                                                            <FormControl>
                                                                <Button
                                                                    variant="outline"
                                                                    role="combobox"
                                                                    aria-expanded={isPractitionerSelectOpen}
                                                                    className="w-full justify-between"
                                                                >
                                                                    {field.value
                                                                        ? availablePractitioners.find((p) => p.id === field.value)?.name || "Unknown"
                                                                        : "None"}
                                                                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                                                </Button>
                                                            </FormControl>
                                                        </PopoverTrigger>
                                                        <PopoverContent className="w-[--radix-popover-trigger-width] max-h-[300px] overflow-y-auto p-0">
                                                            <Command>
                                                                <CommandInput placeholder="Search practitioners..." />
                                                                <CommandList>
                                                                    <CommandGroup>
                                                                        <CommandItem
                                                                            value="none"
                                                                            onSelect={() => field.onChange(null)}
                                                                            className={!field.value ? "bg-accent" : ""}
                                                                        >
                                                                            <span>None</span>
                                                                        </CommandItem>
                                                                        {isLoadingPractitioners ? (
                                                                            <CommandItem disabled>Loading practitioners...</CommandItem>
                                                                        ) : (
                                                                            availablePractitioners.map((practitioner) => (
                                                                                <CommandItem
                                                                                    key={practitioner.id}
                                                                                    value={`${practitioner.name} ${practitioner.categoryName ?? ''} ${practitioner.id}`}
                                                                                    onSelect={() => field.onChange(practitioner.id)}
                                                                                    className={field.value === practitioner.id ? "bg-accent" : ""}
                                                                                >
                                                                                    <PractitionerNameWithCategory
                                                                                        name={practitioner.name}
                                                                                        categoryName={practitioner.categoryName}
                                                                                    />
                                                                                </CommandItem>
                                                                            ))
                                                                        )}
                                                                    </CommandGroup>
                                                                </CommandList>
                                                            </Command>
                                                        </PopoverContent>
                                                    </Popover>
                                                    <FormDescription>
                                                        Select the practitioner this user should be associated with, or choose "None".
                                                    </FormDescription>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />

                                        <Separator className="my-4" />
                                        {/* Save and Discard Buttons */}
                                        <div className="flex items-center gap-2 pt-4">
                                            <Button
                                                variant="outline"
                                                type="button"
                                                onClick={handleDiscardChanges}
                                                disabled={updateUserMutation.isPending || !isDirty}
                                            >
                                                <X className="mr-2 h-4 w-4" />
                                                Discard Changes
                                            </Button>
                                            <Button
                                                type="submit" // This is the form's submit button
                                                disabled={updateUserMutation.isPending || !isDirty}
                                            >
                                                {updateUserMutation.isPending ? (
                                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                                ) : (
                                                    <Save className="mr-2 h-4 w-4" />
                                                )}
                                                Save Changes
                                            </Button>
                                        </div>

                                    </CardContent>
                                    {/* CardFooter can be removed if buttons are in CardContent */}
                                    {/* <CardFooter>
                    ...
                  </CardFooter> */}
                                </Card>
                            </form>
                        </Form>
                    </div>
                </div>
            </div>

            <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete this user?</AlertDialogTitle>
                        <AlertDialogDescription>
                            {userData.firstName} {userData.lastName} will be soft deleted. They will no
                            longer be able to log in anywhere. You can restore the account later.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={deleteUserMutation.isPending}>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={(e) => {
                                e.preventDefault();
                                deleteUserMutation.mutate({ id: userId });
                            }}
                            disabled={deleteUserMutation.isPending}
                            className="bg-destructive text-white hover:bg-destructive/90"
                        >
                            {deleteUserMutation.isPending ? (
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            ) : (
                                <Trash2 className="mr-2 h-4 w-4" />
                            )}
                            Delete User
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <AlertDialog open={isRestoreDialogOpen} onOpenChange={setIsRestoreDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Restore this user?</AlertDialogTitle>
                        <AlertDialogDescription>
                            {userData.firstName} {userData.lastName} will be restored and able to log in
                            again.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={restoreUserMutation.isPending}>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={(e) => {
                                e.preventDefault();
                                restoreUserMutation.mutate({ id: userId });
                            }}
                            disabled={restoreUserMutation.isPending}
                        >
                            {restoreUserMutation.isPending ? (
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            ) : (
                                <RotateCcw className="mr-2 h-4 w-4" />
                            )}
                            Restore User
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </React.Fragment>
    );
};

export default User;